import { ApiParams, prepareBaseListApiParams } from '@/api/helpers/apiHelpers';
import { QueryParamsType } from '@/api/helpers/apiParamsHelpers';
import { prepareResponse, throwResponseError, withPreparedResponse } from '@/helpers/apiHelpers/responseHelpers';
import { IDEMPOTENCY_KEY, idempotencyKeyHandler } from '@/helpers/apiHelpers/requestHelpers';
import { getMyVaultCatalogApi } from '@/api/v1/organization/my-vault/getMyVaultCatalogApi';
import { getVaultAccountsApi, getVaultRegionsApi } from '@/api/v1/organization/my-vault/getVaultAccountsApi';
import { getVaultAccountApi } from '@/api/v1/organization/my-vault/getVaultAccountApi';
import {
  createVaultAccountApi,
  CreateVaultAccountPayloadType,
} from '@/api/v1/organization/my-vault/createVaultAccountApi';
import { editVaultAccountApi, EditVaultAccountPayloadType } from '@/api/v1/organization/my-vault/editVaultAccountApi';
import { getVaultAccountIncrementalFieldsApi } from '@/api/v1/organization/my-vault/getVaultAccountIncrementalFieldsApi';
import {
  addVaultAccountRailsApi,
  AddVaultAccountRailsPayloadType,
} from '@/api/v1/organization/my-vault/addVaultAccountRailsApi';
import { getVaultAccountStatusApi } from '@/api/v1/organization/my-vault/getVaultAccountStatusApi';
import {
  updateVaultAccountStatusApi,
  UpdateVaultAccountStatusPayloadType,
} from '@/api/v1/organization/my-vault/updateVaultAccountStatusApi';
import { getSecureLinksApi } from '@/api/v1/organization/my-vault/getSecureLinksApi';
import { createSecureLinkApi, CreateSecureLinkPayloadType } from '@/api/v1/organization/my-vault/createSecureLinkApi';
import { secureLinkActionApi, SecureLinkActionType } from '@/api/v1/organization/my-vault/secureLinkActionApi';
import { getSecureLinkVerificationApi } from '@/api/v1/organization/my-vault/getSecureLinkVerificationApi';
import { getVaultEventsApi } from '@/api/v1/organization/my-vault/getVaultEventsApi';
import { sendVaultAccountConfirmationCodeApi } from '@/api/v1/organization/my-vault/sendVaultAccountConfirmationCodeApi';

export const MY_VAULT_QUERY_KEYS = {
  catalog: ['my_vault_catalog'],
  regions: ['my_vault_regions'],
  accounts: 'my_vault_accounts',
  account: 'my_vault_account',
  links: 'my_vault_links',
  verification: 'my_vault_link_verification',
  events: 'my_vault_events',
} as const;

export const loadMyVaultCatalog = () => withPreparedResponse(getMyVaultCatalogApi);
export const loadVaultRegions = () => withPreparedResponse(getVaultRegionsApi);
export const loadVaultAccount = (id: string) => withPreparedResponse(() => getVaultAccountApi(id));
export const loadVaultAccountStatus = (id: string) => withPreparedResponse(() => getVaultAccountStatusApi(id));
export const loadIncrementalFields = (id: string, rails: string[]) =>
  withPreparedResponse(() => getVaultAccountIncrementalFieldsApi(id, rails));
export const loadSecureLinkVerification = (id: string) => withPreparedResponse(() => getSecureLinkVerificationApi(id));
export const editVaultAccount = (id: string, payload: EditVaultAccountPayloadType) =>
  withPreparedResponse(() => editVaultAccountApi(id, payload));
export const updateVaultAccountStatus = (id: string, payload: UpdateVaultAccountStatusPayloadType) =>
  withPreparedResponse(() => updateVaultAccountStatusApi(id, payload));
export const runSecureLinkAction = (id: string, action: SecureLinkActionType) =>
  withPreparedResponse(() => secureLinkActionApi(id, action));

export type VaultListFiltersType = QueryParamsType & {
  region?: string;
  q?: string;
  closed?: string;
  archived?: string;
};

const withFilters = (queryParams: VaultListFiltersType, keys: string[]) => {
  const params: ApiParams = prepareBaseListApiParams(queryParams);

  keys.forEach((key) => {
    if (queryParams?.[key]) params[key] = queryParams[key];
  });

  return params;
};

export const loadVaultAccounts = (queryParams: VaultListFiltersType) =>
  withPreparedResponse(() => getVaultAccountsApi(withFilters(queryParams, ['region', 'q', 'closed'])));

export const loadSecureLinks = (queryParams: VaultListFiltersType) =>
  withPreparedResponse(() =>
    getSecureLinksApi(withFilters(queryParams, ['region', 'q', 'archived', 'vault_account_id']))
  );

export const loadVaultEvents = (queryParams: QueryParamsType & { category?: string; q?: string }) =>
  withPreparedResponse(() => getVaultEventsApi(withFilters(queryParams, ['category', 'q'])));

const withIdempotency = async <T>(
  key: keyof typeof IDEMPOTENCY_KEY,
  call: (header) => Promise<{ response?: Response; json?: T }>
) => {
  const { header, clearKey } = idempotencyKeyHandler(IDEMPOTENCY_KEY[key]);

  try {
    const response = await call(header);

    clearKey();

    return prepareResponse(response);
  } catch (e) {
    throwResponseError(e);
  }
};

export const createVaultAccount = (payload: CreateVaultAccountPayloadType) =>
  withIdempotency('CREATE_VAULT_ACCOUNT', (header) => createVaultAccountApi(payload, header));

export const addVaultAccountRails = (id: string, payload: AddVaultAccountRailsPayloadType) =>
  withIdempotency('ADD_VAULT_ACCOUNT_RAILS', (header) => addVaultAccountRailsApi(id, payload, header));

export const createSecureLink = (payload: CreateSecureLinkPayloadType) =>
  withIdempotency('CREATE_SECURE_LINK', (header) => createSecureLinkApi(payload, header));

export const sendVaultConfirmationCode = async (id: string) => {
  try {
    const response = await sendVaultAccountConfirmationCodeApi(id);

    return prepareResponse(response);
  } catch (e) {
    throwResponseError(e);
  }
};
